import { isAction, Middleware, UnknownAction } from '@reduxjs/toolkit'
import { RootState } from '@/redux/store.ts'
import { authSlice, logout } from '@/features/auth/auth.slice.ts'
import { authApi } from '@/features/auth/auth.api.ts'
import { setLocalStorageItem } from '@/utils/local-storage.ts'

/**
 * The caller's own auth state, saved when they first start impersonating, so that stopping can restore it.
 * Impersonating again while impersonated keeps the first one: that is the real user.
 */
export const ORIGINAL_AUTH_STORAGE_KEY = `${authSlice.name}-before-impersonation`

export const hasOriginalAuth = () => localStorage.getItem(ORIGINAL_AUTH_STORAGE_KEY) != null

/** Puts the saved auth state back as the one in use, and reloads so everything refetches with that token */
export const stopImpersonation = () => {
  const original = localStorage.getItem(ORIGINAL_AUTH_STORAGE_KEY)
  if (original == null) return
  localStorage.setItem(authSlice.name, original)
  localStorage.removeItem(ORIGINAL_AUTH_STORAGE_KEY)
  window.location.reload()
}

export const authPersistenceMiddleware: Middleware<UnknownAction, RootState> = ({
  getState,
}) => {
  return (next) => (action: unknown) => {
    // before the impersonated token replaces the state, which is persisted right after
    if (authApi.endpoints.impersonateUser.matchFulfilled(action) && !hasOriginalAuth() && getState().auth?.user) {
      setLocalStorageItem(ORIGINAL_AUTH_STORAGE_KEY, getState().auth)
    }

    const result = next(action)
    const state = getState()
    if (isAction(action) && action.type.startsWith(authSlice.name) && state.auth) {
      setLocalStorageItem(authSlice.name, state.auth)
    }

    if (isAction(action) && action.type === logout.type) {
      localStorage.removeItem(authSlice.name)
      // logging out ends the impersonation too, so the next login does not offer to restore a stale session
      localStorage.removeItem(ORIGINAL_AUTH_STORAGE_KEY)
    }

    return result
  }
}
